<template>
  <div :style="style" v-if="shouldBeDisplayed">
    <!-- Use TizenVideoPlayer for Tizen devices -->
    <TizenVideoPlayer
      v-if="isTizen4and5and6"
      :itemId="videoWidgetItemId"
      :itemType="widget.object.item_type"
      :itemUrl="videoWidgetItemUrl"
      :imageMode="widget.position.data.imageMode || 'cover'"
      :thumbnail="widget.object.thumbnail_url"
      :zIndex="widgetZIndex"
    />

    <!-- Standard video element for other devices -->
    <video
      v-else
      class="widget-video"
      :style="videoStyle"
      loop
      :muted="widget.object.mute"
      texture
      :ref="`widget_video_${widget.position.data.wid}`"
      @click="toggleVideo()"
      :poster="
        isAndroid && widget.object.thumbnail_url
          ? getVideoThumbnailUrl(widget.object.thumbnail_url)
          : undefined
      "
      @posterror="handlePosterError"
    >
      <source :src="videoUrl" :type="widget.object.item_type" @error="handleVideoError" />
      <source
        v-if="!isWeb"
        :src="defaultVideoUrl"
        :type="widget.object.item_type"
        @error="handleDefaultVideoError"
      />
    </video>
  </div>
</template>

<script>
  import templateWidget from '@/components/widgets/templates/mixins/templateWidget';
  import { simpleTypeMixin } from '@/helpers';
  import { getItemLocalUrl, getItemUrl } from '@/utils/mediaAsset';
  import { getVideoWidgetItemId, getVideoWidgetItemUrl } from '@/utils/videoWidget';
  import TizenVideoPlayer from '@/components/playerWidgets/TizenVideoPlayer.vue';
  import { getTizenVersion } from '@/utils/tizen';
  import { logger } from '@/utils/screenEventsLogger';

  export default {
    name: 'VideoWidget',

    mixins: [templateWidget, simpleTypeMixin],

    components: {
      TizenVideoPlayer,
    },

    props: {
      widget: {
        type: Object,
        default: () => ({}),
      },

      show: {
        type: Boolean,
        default: false,
      },
    },

    watch: {
      videoShow: {
        immediate: true,
        handler(shouldShow) {
          if (shouldShow) {
            this.$nextTick(() => {
              const videoRef = this.$refs[`widget_video_${this.widget.position.data.wid}`];
              if (videoRef) {
                videoRef.currentTime = 0;
                this.playVideo();
              }
            });
          }
        },
      },
    },

    computed: {
      style() {
        return Object.assign({}, this.defaultStyling(this.widget), {
          position: 'absolute',
          overflow: 'hidden',
        });
      },

      isWeb() {
        return window.isWeb;
      },

      isAndroid() {
        return window.isAndroid;
      },

      isTizen() {
        return !!window.isTizen;
      },

      isTizen4and5and6() {
        const tizen_version = getTizenVersion();
        return this.isTizen && (isNaN(tizen_version) || tizen_version < 6.5);
      },

      videoWidgetItemId() {
        return getVideoWidgetItemId(this.widget);
      },

      videoWidgetItemUrl() {
        return getVideoWidgetItemUrl(this.widget);
      },

      widgetZIndex() {
        return 100 + (this.widget.position?.zIndex || 0);
      },

      videoUrl() {
        return getItemLocalUrl(
          getVideoWidgetItemUrl(this.widget),
          getVideoWidgetItemId(this.widget),
        );
      },

      defaultVideoUrl() {
        return getItemUrl(getVideoWidgetItemUrl(this.widget));
      },

      videoStyle() {
        return {
          objectFit: this.widget.position.data.imageMode || 'cover',
        };
      },

      shouldBeDisplayed() {
        // If Tizen, follow the videoShow logic.
        // Too many issues with preloading video in any Tizen version
        if (this.isTizen4and5and6) {
          return this.videoShow;
        }
        // For other platforms, always show
        return true;
      },

      videoShow() {
        return !!this.$store.getters.getTemplateSlideVisibility(this.widget.assoc_id);
      },
    },

    methods: {
      playVideo() {
        this.$refs[`widget_video_${this.widget.position.data.wid}`].currentTime = 0;
        const playPromise = this.$refs[`widget_video_${this.widget.position.data.wid}`].play();

        // browsers released before 2019 may not return a value
        if (playPromise !== undefined) {
          playPromise.then((_) => {}).catch((_error) => {});
        }
      },

      toggleVideo() {
        if (this.isPlaying) {
          this.playVideo();
        } else {
          this.pauseVideo();
        }
      },

      pauseVideo() {
        this.$refs[`widget_video_${this.widget.position.data.wid}`].pause();
      },

      handleVideoError() {
        logger.addLog(`Failed to load video: ${this.videoUrl}`);
      },

      handleDefaultVideoError() {
        logger.addLog(`Failed to load default video: ${this.defaultVideoUrl}`);
      },

      showImage() {
        //this.$emit('show-enlarged', this.widget)
      },
      handlePosterError() {
        logger.addLog(`Failed to load poster: ${thumbnailUrl.value}`);
      },
      getVideoThumbnailUrl(thumbnailUrl) {
        return getItemUrl(thumbnailUrl);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .widget-video {
    width: 100%;
    height: 100%;
  }
</style>
